import { useContext } from "react"
import {
  SET_COST,
  SET_COST_REDUCTION,
  SET_DISCOUNT_AMOUNT,
  SET_DISCOUNT_PERCENTAGE,
  SET_EURO_REDUCED,
  SET_FORMULA,
  SET_INITIAL_RIGHT_FOOTER_DATA,
  SET_INITIAL_VALUES,
  SET_MARGIN_EURO,
  SET_MARGIN_PERCENT,
  SET_PRICE_REDUCED,
  SET_QUANTITY,
  SET_TOTAL_COST,
  SET_TOTAL_PRICE,
  SET_TOTAL_PRICE_WITH_DISCOUNT,
  SET_TOTAL_REDUCED,
  SET_UNIT_PRICE,
  SET_UNIT_PRICE_WITH_DISCOUNT,
} from "context/types"
import { ModalPartidaContext } from "context/modalPartidas/ModalPartidaContext"
import { COST } from "components/Budgeting2023/options/margin/FormulasTab"
import { isNil } from "lodash"
import {
  calcMargin,
  applyCostMargin,
  applyClientMargin,
  applyCostReduction,
} from "utils/partUtils"

export const useModalPartida = () => {
  const context = useContext(ModalPartidaContext)
  if (!context) {
    throw new Error(
      "useModalPartida must be used within a ModalPartidaProvider",
    )
  }

  const { state, dispatch } = context

  const getMargin = partida => {
    if (partida.hasOwnProperty("margin") && !isNil(partida.margin)) {
      return parseFloat(partida.margin)
    } else {
      return (
        parseFloat(partida.costeGeneral) +
        parseFloat(partida.costeIndirecto) +
        parseFloat(partida.industrialProfit)
      )
    }
  }

  const loadInitialRightFooterData = (partida, formula) => {
    dispatch({ type: SET_FORMULA, payload: formula })

    const marginPercent = getMargin(partida)

    const unitPrice = partida.clientPriceWithoutDiscount
    const totalCost = partida.price * partida.quantity
    const totalReduced = partida.priceReduced * partida.quantity
    const totalPrice = partida.clientPriceWithoutDiscount * partida.quantity
    const marginEuro = totalPrice - totalReduced
    const euroReduced = totalCost - totalReduced

    const { discountAmount, unitPriceWithDiscount, totalPriceWithDiscount } =
      calculateDiscountPrices(unitPrice, partida.quantity, partida.discount)

    dispatch({
      type: SET_INITIAL_RIGHT_FOOTER_DATA,
      payload: {
        rightFooter: {
          marginPercent: isNaN(marginPercent) ? 0 : marginPercent,
          unitPrice: isNaN(unitPrice) ? 0 : unitPrice,
          marginEuro: isNaN(marginEuro) ? 0 : marginEuro,
          totalPrice: isNaN(totalPrice) ? 0 : totalPrice,
          discountPercentage: partida.discount || 0,
          discountAmount: isNaN(discountAmount) ? 0 : discountAmount,
          unitPriceWithDiscount: isNaN(unitPriceWithDiscount)
            ? 0
            : unitPriceWithDiscount,
          totalPriceWithDiscount: isNaN(totalPriceWithDiscount)
            ? 0
            : totalPriceWithDiscount,
        },
      },
    })

    dispatch({
      type: SET_INITIAL_VALUES,
      payload: {
        cost: isNaN(partida.price) ? 0 : partida.price,
        totalCost: isNaN(totalCost) ? 0 : totalCost,
        quantity: isNaN(partida.quantity) ? 0 : parseFloat(partida.quantity),
        costReduction: partida.costReduction || 0,
        priceReduced: partida.priceReduced || 0,
        euroReduced: isNaN(euroReduced) ? 0 : euroReduced,
        totalReduced: isNaN(totalReduced) ? 0 : totalReduced,
      },
    })
  }

  const calcClientPrice = (price, formula, marginPercent) => {
    if (formula === COST) {
      return applyCostMargin(price, marginPercent)
    } else {
      return applyClientMargin(price, marginPercent)
    }
  }

  const setMarginPercent = value => {
    dispatchMarginPercent(value)

    const precioUnitario = calcClientPrice(
      state.leftGridContent.priceReduced,
      state.formula,
      state.rightFooter.marginPercent,
    )

    dispatchUnitPrice(precioUnitario)

    const totalPrice = precioUnitario * state.leftGridContent.quantity

    dispatchTotalPrice(totalPrice)

    const marginEuro =
      totalPrice -
      state.leftGridContent.priceReduced * state.leftGridContent.quantity

    dispatchMarginEuro(marginEuro)

    calculateDiscountPrices(
      precioUnitario,
      state.leftGridContent.quantity,
      state.rightFooter.discountPercentage,
    )
  }

  const dispatchMarginPercent = marginPercent => {
    const parsed = parseFloat(marginPercent)
    dispatch({
      type: SET_MARGIN_PERCENT,
      payload: {
        marginPercent: isFinite(parsed) ? parsed : 0,
      },
    })
  }

  const setMarginWithFormula = value => {
    const marginPercent = calcMargin(
      state.leftGridContent.priceReduced,
      value,
      state.formula,
    )
    dispatchMarginPercent(marginPercent)
  }

  const setUnitPrice = value => {
    dispatchUnitPrice(value)
    const unitPrice = parseFloat(value)

    setMarginWithFormula(unitPrice)
    // calcular margen euro
    const marginEuro =
      (unitPrice - state.leftGridContent.priceReduced) *
      state.leftGridContent.quantity
    dispatchMarginEuro(marginEuro)

    // calcular precio total
    const totalPrice = unitPrice * state.leftGridContent.quantity
    dispatchTotalPrice(totalPrice)

    calculateDiscountPrices(
      unitPrice,
      state.leftGridContent.quantity,
      state.rightFooter.discountPercentage,
    )
  }

  const dispatchUnitPrice = unitPrice => {
    const parsed = parseFloat(unitPrice)
    dispatch({
      type: SET_UNIT_PRICE,
      payload: {
        unitPrice: isFinite(parsed) ? parsed : 0,
      },
    })
  }

  const setMarginEuro = value => {
    dispatchMarginEuro(value)
    const marginEuro = parseFloat(value)

    // calcular margen percent
    const unitMargin = marginEuro / state.leftGridContent.quantity

    const clientPrice = state.leftGridContent.priceReduced + unitMargin
    dispatchUnitPrice(clientPrice)

    const totalPrice = clientPrice * state.leftGridContent.quantity
    dispatchTotalPrice(totalPrice)

    if (state.formula === COST) {
      const marginPercent =
        (unitMargin / state.leftGridContent.priceReduced) * 100
      dispatchMarginPercent(marginPercent)
    } else {
      const marginPercent = (unitMargin / clientPrice) * 100
      dispatchMarginPercent(marginPercent)
    }

    calculateDiscountPrices(
      clientPrice,
      state.leftGridContent.quantity,
      state.rightFooter.discountPercentage,
    )
  }

  const dispatchMarginEuro = value => {
    const parsed = parseFloat(value)
    dispatch({
      type: SET_MARGIN_EURO,
      payload: {
        marginEuro: isFinite(parsed) ? parsed : 0,
      },
    })
  }

  const dispatchTotalPrice = value => {
    const parsed = parseFloat(value)
    dispatch({
      type: SET_TOTAL_PRICE,
      payload: {
        totalPrice: isFinite(parsed) ? parsed : 0,
      },
    })
  }

  const dispatchQuantity = value => {
    const parsed = parseFloat(value)
    dispatch({
      type: SET_QUANTITY,
      payload: {
        quantity: isFinite(parsed) ? parsed : 0,
      },
    })
  }

  const setQuantityAndRecalculate = value => {
    dispatchQuantity(value)
    const quantity = parseFloat(value)

    // calcular el total cost
    const totalCost = state.leftGridContent.cost * quantity
    dispatchTotalCost(totalCost)

    const totalReduced = state.leftGridContent.priceReduced * quantity
    dispatchTotalReduced(totalReduced)

    const euroReduced = totalCost - totalReduced
    dispatchEuroReduced(euroReduced)

    const marginPercent = state.rightFooter.marginPercent
    const unitPrice =
      state.formula === COST
        ? applyCostMargin(state.leftGridContent.priceReduced, marginPercent)
        : applyClientMargin(state.leftGridContent.priceReduced, marginPercent)
    dispatchUnitPrice(unitPrice)

    const totalPrice = unitPrice * quantity
    dispatchTotalPrice(totalPrice)

    // calcular el margen euro
    const marginEuro = totalPrice - totalReduced
    dispatchMarginEuro(marginEuro)

    calculateDiscountPrices(
      unitPrice,
      quantity,
      state.rightFooter.discountPercentage,
    )
  }

  const dispatchCost = value => {
    const parsed = parseFloat(value ?? 0)
    dispatch({
      type: SET_COST,
      payload: {
        cost: isFinite(parsed) ? parsed : 0,
      },
    })
  }

  const setCostAndRecalculate = value => {
    dispatchCost(value)
    const cost = parseFloat(value)

    // calcular el total cost
    const totalCost = cost * state.leftGridContent.quantity
    dispatchTotalCost(totalCost)

    // calcular el Price Reduced
    const priceReduced = applyCostReduction(
      cost,
      state.leftGridContent.costReduction,
    )
    dispatchPriceReduced(priceReduced)

    const totalReduced = priceReduced * state.leftGridContent.quantity

    dispatchTotalReduced(totalReduced)

    // calcular el precio unitario
    let unitPrice = 0
    if (state.formula === COST) {
      unitPrice = applyCostMargin(priceReduced, state.rightFooter.marginPercent)
    } else {
      unitPrice = applyClientMargin(
        priceReduced,
        state.rightFooter.marginPercent,
      )
    }

    dispatchUnitPrice(unitPrice)

    // calcular el precio total
    const totalPrice = unitPrice * state.leftGridContent.quantity
    dispatchTotalPrice(totalPrice)

    const marginEuro = totalPrice - totalReduced

    dispatchMarginEuro(marginEuro)

    calculateDiscountPrices(
      unitPrice,
      state.leftGridContent.quantity,
      state.rightFooter.discountPercentage,
    )
  }

  const dispatchTotalCost = value => {
    const parsed = parseFloat(value)
    dispatch({
      type: SET_TOTAL_COST,
      payload: { totalCost: isFinite(parsed) ? parsed : 0 },
    })
  }

  const setTotalCostAndRecalculate = value => {
    dispatchTotalCost(parseFloat(value))
    const totalCost = parseFloat(value)

    let unitCost = totalCost
    if (state.leftGridContent.quantity !== 0) {
      unitCost =
        parseFloat(totalCost) / parseFloat(state.leftGridContent.quantity)
    }

    setCostAndRecalculate(unitCost)
  }

  const dispatchCostReduction = value => {
    const parsed = parseFloat(value)
    dispatch({
      type: SET_COST_REDUCTION,
      payload: { costReduction: isFinite(parsed) ? parsed : 0 },
    })
  }

  const setCostReductionAndRecalculate = value => {
    dispatchCostReduction(parseFloat(value))
    const costReduction = parseFloat(value)
    const priceReduced = applyCostReduction(
      state.leftGridContent.cost,
      costReduction,
    )

    dispatchPriceReduced(priceReduced)

    // calcular el total reduced
    const totalReduced = priceReduced * state.leftGridContent.quantity
    dispatchTotalReduced(totalReduced)

    // calcular el euro reduced
    const euroReduced = state.leftGridContent.totalCost - totalReduced
    dispatchEuroReduced(euroReduced)

    // calcular el precio unitario
    let unitPrice = 0
    if (state.formula === COST) {
      unitPrice = applyCostMargin(priceReduced, state.rightFooter.marginPercent)
    } else {
      unitPrice = applyClientMargin(
        priceReduced,
        state.rightFooter.marginPercent,
      )
    }

    dispatchUnitPrice(unitPrice)

    // calcular el precio total
    const totalPrice = unitPrice * state.leftGridContent.quantity
    dispatchTotalPrice(totalPrice)

    // calcular el margen euro
    const marginEuro =
      totalPrice - priceReduced * state.leftGridContent.quantity

    dispatchMarginEuro(marginEuro)

    calculateDiscountPrices(
      unitPrice,
      state.leftGridContent.quantity,
      state.rightFooter.discountPercentage,
    )
  }

  const dispatchPriceReduced = value => {
    const parsed = parseFloat(value)
    dispatch({
      type: SET_PRICE_REDUCED,
      payload: { priceReduced: isFinite(parsed) ? parsed : 0 },
    })
  }

  const dispatchEuroReduced = value => {
    const parsed = parseFloat(value)
    dispatch({
      type: SET_EURO_REDUCED,
      payload: { euroReduced: isFinite(parsed) ? parsed : 0 },
    })
  }

  const dispatchTotalReduced = value => {
    const parsed = parseFloat(value)
    dispatch({
      type: SET_TOTAL_REDUCED,
      payload: { totalReduced: isFinite(parsed) ? parsed : 0 },
    })
  }

  const dispatchDiscountPercentage = value => {
    const parsed = parseFloat(value)
    dispatch({
      type: SET_DISCOUNT_PERCENTAGE,
      payload: { discountPercentage: isFinite(parsed) ? parsed : 0 },
    })
  }

  const setDiscountPercentage = value => {
    dispatchDiscountPercentage(value)
    const discountPercentage = parseFloat(value)

    calculateDiscountPrices(
      state.rightFooter.unitPrice,
      state.leftGridContent.quantity,
      discountPercentage,
    )
  }

  const dispatchDiscountAmount = value => {
    const parsed = parseFloat(value)
    dispatch({
      type: SET_DISCOUNT_AMOUNT,
      payload: { discountAmount: isFinite(parsed) ? parsed : 0 },
    })
  }

  const dispatchUnitPriceWithDiscount = value => {
    const parsed = parseFloat(value)
    dispatch({
      type: SET_UNIT_PRICE_WITH_DISCOUNT,
      payload: { unitPriceWithDiscount: isFinite(parsed) ? parsed : 0 },
    })
  }

  const dispatchTotalPriceWithDiscount = value => {
    const parsed = parseFloat(value)
    dispatch({
      type: SET_TOTAL_PRICE_WITH_DISCOUNT,
      payload: { totalPriceWithDiscount: isFinite(parsed) ? parsed : 0 },
    })
  }

  const calculateDiscountPrices = (unitPrice, quantity, discountPercentage) => {
    // Calcular el descuento en euros
    const discountAmount = (unitPrice * discountPercentage) / 100
    dispatchDiscountAmount(discountAmount)

    // Calcular el precio unitario con descuento
    const unitPriceWithDiscount = unitPrice - discountAmount
    dispatchUnitPriceWithDiscount(unitPriceWithDiscount)

    // Calcular el total con descuento
    const totalPriceWithDiscount = unitPriceWithDiscount * quantity
    dispatchTotalPriceWithDiscount(totalPriceWithDiscount)

    return {
      discountAmount,
      unitPriceWithDiscount,
      totalPriceWithDiscount,
    }
  }

  return {
    state,
    loadInitialRightFooterData,
    setMarginPercent,
    dispatchMarginPercent,
    setUnitPrice,
    dispatchUnitPrice,
    setMarginEuro,
    dispatchMarginEuro,
    dispatchTotalPrice,
    dispatchQuantity,
    setQuantityAndRecalculate,
    dispatchCost,
    setCostAndRecalculate,
    dispatchTotalCost,
    setTotalCostAndRecalculate,
    dispatchCostReduction,
    setCostReductionAndRecalculate,
    dispatchDiscountPercentage,
    setDiscountPercentage,
  }
}
